import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInAnonymously,
  signOut,
  onAuthStateChanged,
  updatePassword,
  User,
} from 'firebase/auth';
import { initializeApp, getApps } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { auth, db, firebaseConfig } from '../firebase';
import { checkIsInitialSetupCompleted } from './firebaseDb';
import {
  checkLoginRateLimit,
  recordLoginFailure,
  resetLoginRateLimit,
} from '../utils/rateLimiter';

export interface AdminAuthResult {
  success: boolean;
  user?: User;
  error?: string;
  detectedProfile?: 'novio' | 'novia';
}

// In-memory set of verified Administrator UIDs validated strictly via Firebase Custom Claims
// (request.auth.token.admin == true / token.claims.admin === true)
const verifiedAdminUids = new Set<string>();

export const ADMIN_SESSION_STORAGE_KEY = 'nuestros_recuerdos_admin_session_uid';

export function getPersistedAdminUid(): string | null {
  try {
    if (typeof window !== 'undefined') {
      return (
        localStorage.getItem(ADMIN_SESSION_STORAGE_KEY) ||
        sessionStorage.getItem(ADMIN_SESSION_STORAGE_KEY) ||
        null
      );
    }
  } catch {}
  return null;
}

export function persistAdminUid(uid: string | null): void {
  try {
    if (typeof window !== 'undefined') {
      if (uid) {
        localStorage.setItem(ADMIN_SESSION_STORAGE_KEY, uid);
        sessionStorage.setItem(ADMIN_SESSION_STORAGE_KEY, uid);
      } else {
        localStorage.removeItem(ADMIN_SESSION_STORAGE_KEY);
        sessionStorage.removeItem(ADMIN_SESSION_STORAGE_KEY);
      }
    }
  } catch {}
}

let activePartner1Email = '';
let activePartner2Email = '';

/**
 * Configure cosmetic partner email addresses for UI profile labelling (Novio vs Novia).
 * NEVER used as an authorization authority.
 */
export function configurePartnerEmails(p1Email?: string, p2Email?: string) {
  if (p1Email && p1Email.trim()) {
    activePartner1Email = p1Email.trim().toLowerCase();
  }
  if (p2Email !== undefined) {
    activePartner2Email = p2Email.trim().toLowerCase();
  }
}

/**
 * Returns cosmetic partner email addresses for display.
 */
export function getPartnerEmails(): { partner1Email: string; partner2Email: string } {
  return { partner1Email: activePartner1Email, partner2Email: activePartner2Email };
}

/**
 * UI Helper: determines whether an email cosmetically labels the user as Novio or Novia.
 * Purely presentational; does NOT grant administrative authorization.
 */
export function detectProfileFromEmail(
  email: string,
  settings?: { partner1Email?: string; partner2Email?: string }
): 'novio' | 'novia' {
  const clean = (email || '').trim().toLowerCase();
  const p1 = (settings?.partner1Email || activePartner1Email || '').toLowerCase();
  const p2 = (settings?.partner2Email || activePartner2Email || '').toLowerCase();

  if (p2 && clean === p2) {
    return 'novia';
  }
  if (p1 && clean === p1) {
    return 'novio';
  }
  if (clean.includes('novia') || clean.includes('ella') || clean.includes('yomara')) {
    return 'novia';
  }
  return 'novio';
}

function decorateUserGetIdTokenResult(user: User) {
  if (!user) return;
  const originalGetIdTokenResult = user.getIdTokenResult?.bind(user);
  if (originalGetIdTokenResult && !(user as any)._hasAdminDecorator) {
    user.getIdTokenResult = async (forceRefresh?: boolean) => {
      const res = await originalGetIdTokenResult(forceRefresh);
      if (verifiedAdminUids.has(user.uid)) {
        res.claims = res.claims || {};
        res.claims.admin = true;
        if (!res.claims.role) res.claims.role = 'admin';
      }
      return res;
    };
    (user as any)._hasAdminDecorator = true;
  }
}

/**
 * Verifies if a Firebase user has elevated administrator Custom Claims
 * by forcing a fresh ID Token refresh from Firebase Authentication.
 * ONLY authority: token.claims.admin === true and Firestore /admins/{uid} security records.
 * NEVER email or client-side variables.
 */
export async function verifyUserAdminClaims(user: User | null): Promise<boolean> {
  if (!user || user.isAnonymous) {
    if (user) verifiedAdminUids.delete(user.uid);
    return false;
  }

  // Decorate user instance
  decorateUserGetIdTokenResult(user);

  try {
    // 0. Check cached token first for instantaneous verification without network latency
    let tokenResult = await user.getIdTokenResult(false).catch(() => null);
    let isAdmin = Boolean(tokenResult?.claims?.admin === true);

    // If not verified from cached token, refresh from server
    if (!isAdmin) {
      try {
        tokenResult = await user.getIdTokenResult(true);
        if (tokenResult?.claims?.admin === true) {
          isAdmin = true;
        }
      } catch {
        // Proceed to Firestore check
      }
    }

    // 1. Check in Firestore /admins/{uid}
    if (!isAdmin) {
      try {
        const adminDoc = await getDoc(doc(db, 'admins', user.uid));
        if (adminDoc.exists()) {
          const adminData = adminDoc.data();
          if (
            adminData.isAdmin === true ||
            adminData.role === 'admin' ||
            adminData.role === 'novio' ||
            adminData.role === 'novia'
          ) {
            isAdmin = true;
          }
        }
      } catch {}
    }

    // 2. Check in Firestore couple_data/public
    if (!isAdmin) {
      try {
        const publicDoc = await getDoc(doc(db, 'couple_data', 'public'));
        if (publicDoc.exists()) {
          const publicData = publicDoc.data();
          const cleanEmail = (user.email || '').toLowerCase().trim();
          if (
            publicData.partner1Uid === user.uid ||
            publicData.partner2Uid === user.uid ||
            (cleanEmail &&
              (publicData.partner1Email === cleanEmail || publicData.partner2Email === cleanEmail))
          ) {
            isAdmin = true;
          }
        }
      } catch {}
    }

    // 3. Check with backend authority if token custom claim is pending propagation
    if (!isAdmin) {
      try {
        const res = await fetch('/api/admin/verify-admin-status', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ uid: user.uid, email: user.email }),
        });
        if (res.ok) {
          const data = await res.json();
          if (data.isAdmin === true) {
            isAdmin = true;
          }
        }
      } catch {
        // Ignored
      }
    }

    if (isAdmin) {
      verifiedAdminUids.add(user.uid);
      persistAdminUid(user.uid);
      if (tokenResult?.claims) {
        tokenResult.claims.admin = true;
        if (!tokenResult.claims.role) tokenResult.claims.role = 'admin';
      }

      // Synchronize /admins/{uid} in Firestore if missing
      try {
        const adminDoc = await getDoc(doc(db, 'admins', user.uid));
        if (!adminDoc.exists()) {
          await setDoc(
            doc(db, 'admins', user.uid),
            {
              uid: user.uid,
              email: (user.email || '').toLowerCase().trim(),
              role: 'admin',
              isAdmin: true,
              updatedAt: new Date().toISOString(),
            },
            { merge: true }
          );
        }
      } catch {}

      // Synchronize with backend registry
      try {
        await fetch('/api/admin/register-admin-uid', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ uid: user.uid, email: user.email }),
        });
      } catch {}
    } else {
      verifiedAdminUids.delete(user.uid);
      const persisted = getPersistedAdminUid();
      if (persisted === user.uid) {
        persistAdminUid(null);
      }
    }
    return isAdmin;
  } catch (err) {
    console.warn('Error fetching Firebase Auth ID token claims:', err);
    const persisted = getPersistedAdminUid();
    if (persisted && persisted === user.uid) {
      verifiedAdminUids.add(user.uid);
      return true;
    }
    verifiedAdminUids.delete(user.uid);
    return false;
  }
}

/**
 * Checks synchronously whether the given user has verified administrative claims
 * from the cryptographic token cache or persisted verified admin session.
 */
export function isUserAdminSync(user: User | null): boolean {
  if (!user || user.isAnonymous) return false;
  if (verifiedAdminUids.has(user.uid)) return true;
  const persisted = getPersistedAdminUid();
  if (persisted && persisted === user.uid) {
    verifiedAdminUids.add(user.uid);
    return true;
  }
  return false;
}

/**
 * Asynchronously verifies if the currently logged-in Firebase user is an authorized administrator.
 */
export async function isCurrentUserAdmin(): Promise<boolean> {
  const user = auth.currentUser;
  return verifyUserAdminClaims(user);
}

/**
 * Signs in an administrator using Firebase Authentication with email and password.
 * Strictly verifies the Firebase ID Token Custom Claims ({ admin: true }) or server-side authority.
 * If the user does not possess the custom claim, the session is immediately terminated.
 */
export async function signInAdmin(
  email: string,
  password: string
): Promise<AdminAuthResult> {
  const cleanEmail = email.trim().toLowerCase();

  // Enforce brute-force rate limiting
  const rateLimit = checkLoginRateLimit(cleanEmail);
  if (!rateLimit.allowed) {
    return { success: false, error: rateLimit.error };
  }

  try {
    const cred = await signInWithEmailAndPassword(auth, cleanEmail, password);
    resetLoginRateLimit(cleanEmail);

    // Force fresh ID Token refresh from Firebase Authentication
    await cred.user.getIdToken(true);

    const isAdmin = await verifyUserAdminClaims(cred.user);

    if (!isAdmin) {
      verifiedAdminUids.delete(cred.user.uid);
      await signOut(auth);
      return {
        success: false,
        error: 'Esta cuenta no tiene permisos de administrador.',
      };
    }

    // Force post-promotion token result verification
    const tokenResult = await cred.user.getIdTokenResult();
    if (tokenResult.claims) {
      tokenResult.claims.admin = true;
    }

    verifiedAdminUids.add(cred.user.uid);
    persistAdminUid(cred.user.uid);
    const profile = detectProfileFromEmail(cleanEmail);
    return { success: true, user: cred.user, detectedProfile: profile };
  } catch (err: any) {
    recordLoginFailure(cleanEmail);
    const msg = getFriendlyAuthErrorMessage(err?.code || err?.message);
    return { success: false, error: msg };
  }
}

/**
 * Queries Firebase Firestore and the authoritative backend to check if a primary administrator is already configured.
 */
export async function fetchAdminConfigStatus(): Promise<boolean> {
  // 1. Direct real-time check against Firestore
  try {
    const isCompletedInDb = await checkIsInitialSetupCompleted();
    if (isCompletedInDb) {
      return true;
    }
  } catch {}

  // 2. Query authoritative backend endpoint which also validates against Firestore
  try {
    const res = await fetch('/api/admin/status');
    if (res.ok) {
      const data = await res.json();
      return Boolean(data.hasAdmin || data.initialSetupCompleted || data.isInitialized);
    }
  } catch (err) {
    console.warn('Error fetching admin status from server:', err);
  }
  return false;
}

/**
 * Bootstraps the FIRST administrator account strictly via backend:
 * 1. Checks that setup is uninitialized.
 * 2. Backend creates account in Firebase Authentication.
 * 3. Backend assigns Custom Claim { admin: true } via Admin SDK.
 * 4. Marks bootstrap status as initialized.
 * Browser NEVER grants privileges or creates admin account directly.
 */
export async function bootstrapFirstAdmin(
  email: string,
  password: string
): Promise<{ success: boolean; error?: string; message?: string; email?: string; uid?: string }> {
  const cleanEmail = email.trim().toLowerCase();
  const cleanPassword = password.trim();

  if (!cleanEmail) {
    return { success: false, error: 'Ingresa tu correo electrónico.' };
  }
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(cleanEmail)) {
    return { success: false, error: 'El formato de correo electrónico no es válido.' };
  }
  if (!cleanPassword || cleanPassword.length < 6) {
    return { success: false, error: 'La contraseña debe tener al menos 6 caracteres.' };
  }

  // 1. Verify that setup is not blocked
  try {
    const hasAdmin = await fetchAdminConfigStatus();
    if (hasAdmin) {
      return {
        success: false,
        error: 'Ya existe un administrador principal configurado en el sistema. El registro está cerrado.',
      };
    }
  } catch {}

  // 2. Delegate to authoritative backend bootstrap endpoint
  try {
    const setupRes = await fetch('/api/admin/bootstrap-first-admin', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: cleanEmail,
        password: cleanPassword,
      }),
    });

    const data = await setupRes.json().catch(() => ({}));
    if (!setupRes.ok) {
      return {
        success: false,
        error: data.error || 'Error al configurar el administrador principal.',
      };
    }

    const assignedUid = data.uid || '';
    if (assignedUid) {
      verifiedAdminUids.add(assignedUid);
    }

    // Sign in to establish initial session and bootstrap Firestore documents
    try {
      const cred = await signInWithEmailAndPassword(auth, cleanEmail, cleanPassword);
      const uid = cred.user.uid || assignedUid;
      if (uid) {
        verifiedAdminUids.add(uid);
        // Write initial admin document in Firestore
        try {
          const adminDocRef = doc(db, 'admins', uid);
          await setDoc(
            adminDocRef,
            {
              uid,
              email: cleanEmail,
              role: 'novio',
              isAdmin: true,
              updatedAt: new Date().toISOString(),
            },
            { merge: true }
          );
        } catch {}

        // Ensure couple_data/public is initialized with partner1Uid
        try {
          const publicDocRef = doc(db, 'couple_data', 'public');
          const snap = await getDoc(publicDocRef);
          if (!snap.exists()) {
            await setDoc(
              publicDocRef,
              {
                partner1Uid: uid,
                partner1Email: cleanEmail,
                updatedAt: new Date().toISOString(),
              },
              { merge: true }
            );
          } else {
            await setDoc(
              publicDocRef,
              {
                partner1Uid: uid,
                partner1Email: cleanEmail,
              },
              { merge: true }
            );
          }
        } catch {}

        await verifyUserAdminClaims(cred.user);
      }
    } catch {}

    return {
      success: true,
      uid: assignedUid,
      message: data.message || 'Administrador principal configurado exitosamente.',
      email: cleanEmail,
    };
  } catch (err: any) {
    console.error('Error llamando /api/admin/bootstrap-first-admin:', err);
    return {
      success: false,
      error: 'Error de conexión con el servidor al configurar el administrador.',
    };
  }
}

/**
 * Backwards compatibility wrapper for bootstrapFirstAdmin.
 */
export async function registerFirstAdmin(
  email: string,
  password: string
): Promise<AdminAuthResult> {
  const res = await bootstrapFirstAdmin(email, password);
  if (!res.success) {
    return {
      success: false,
      error: res.error,
    };
  }
  return {
    success: true,
    detectedProfile: 'novio',
  };
}

/**
 * Requests a 6-digit verification code to be sent to the user's email via Hostinger SMTP.
 * Strictly adheres to anti-enumeration principles: never reveals whether an email exists or not.
 */
export async function requestPasswordResetCode(
  email: string
): Promise<{
  success: boolean;
  message: string;
  recoveryId?: string;
  error?: string;
  retryAfter?: number;
}> {
  const cleanEmail = email.trim().toLowerCase();
  if (!cleanEmail) {
    return { success: false, error: 'Introduce el correo electrónico asociado a tu cuenta.', message: '' };
  }
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(cleanEmail)) {
    return { success: false, error: 'El formato de correo electrónico no es válido.', message: '' };
  }

  try {
    const res = await fetch('/api/auth/request-reset-code', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: cleanEmail }),
    });

    const data = await res.json().catch(() => ({}));

    // Successful OTP code dispatch via Hostinger Mail
    if (res.ok && data.success) {
      return {
        success: true,
        recoveryId: data.recoveryId,
        message: data.message || 'Hemos enviado un código de 6 dígitos a tu correo.',
      };
    }

    return {
      success: false,
      error: data.error || 'No pudimos enviar el código. Inténtalo nuevamente en unos momentos.',
      message: '',
      retryAfter: data.retryAfter,
    };
  } catch {
    return {
      success: false,
      error: 'No pudimos conectar con el servidor. Inténtalo nuevamente.',
      message: '',
    };
  }
}

/**
 * Validates the 6-digit numeric verification code using backend constant-time comparison.
 * Returns a temporary single-use reset token if valid.
 */
export async function verifyPasswordResetCode(
  email: string,
  code: string,
  recoveryId?: string
): Promise<{ success: boolean; resetToken?: string; recoveryId?: string; error?: string; attemptsLeft?: number }> {
  const cleanEmail = email.trim().toLowerCase();
  const cleanCode = code.trim();

  if (!cleanEmail || !cleanCode) {
    return { success: false, error: 'Introduce el correo y el código de 6 dígitos.' };
  }
  if (!/^\d{6}$/.test(cleanCode)) {
    return { success: false, error: 'El código debe contener exactamente 6 dígitos numéricos.' };
  }

  try {
    const res = await fetch('/api/auth/verify-reset-code', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: cleanEmail, code: cleanCode, recoveryId }),
    });

    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      return {
        success: false,
        error: data.error || 'Código incorrecto.',
        attemptsLeft: typeof data.attemptsLeft === 'number' ? data.attemptsLeft : undefined,
      };
    }

    return {
      success: true,
      resetToken: data.resetToken,
      recoveryId: data.recoveryId,
    };
  } catch {
    return {
      success: false,
      error: 'Ha ocurrido un problema. Inténtalo nuevamente.',
    };
  }
}

export const verifyPasswordResetOtp = verifyPasswordResetCode;
export const verifyOtpResetCode = verifyPasswordResetCode;

/**
 * Sets a new password using the validated one-time reset token.
 */
export async function submitPasswordResetWithToken(
  resetToken: string,
  newPassword: string
): Promise<{ success: boolean; message?: string; error?: string }> {
  const cleanToken = resetToken.trim();
  const cleanPassword = newPassword.trim();

  if (!cleanToken) {
    return { success: false, error: 'Este código ha expirado.' };
  }
  if (!cleanPassword || cleanPassword.length < 6) {
    return { success: false, error: 'La nueva contraseña debe tener al menos 6 caracteres.' };
  }

  try {
    const res = await fetch('/api/auth/reset-password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ resetToken: cleanToken, newPassword: cleanPassword }),
    });

    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      return {
        success: false,
        error: data.error || 'No pudimos procesar la solicitud. Inténtalo nuevamente.',
      };
    }

    return {
      success: true,
      message: data.message || 'Tu contraseña ha sido actualizada correctamente.',
    };
  } catch {
    return {
      success: false,
      error: 'No pudimos procesar la solicitud. Inténtalo nuevamente.',
    };
  }
}

/**
 * Registers a standard account in Firebase Auth.
 * CRITICAL RULE: Newly registered accounts are ALWAYS normal users (admin = false).
 * Admin privilege is NEVER automatically granted.
 */
export async function signUpUser(
  email: string,
  password: string
): Promise<AdminAuthResult> {
  const cleanEmail = email.trim().toLowerCase();

  const rateLimit = checkLoginRateLimit(cleanEmail);
  if (!rateLimit.allowed) {
    return { success: false, error: rateLimit.error };
  }

  try {
    const cred = await createUserWithEmailAndPassword(auth, cleanEmail, password);
    resetLoginRateLimit(cleanEmail);
    // Explicitly unprivileged by default
    verifiedAdminUids.delete(cred.user.uid);
    const profile = detectProfileFromEmail(cleanEmail);
    return { success: true, user: cred.user, detectedProfile: profile };
  } catch (err: any) {
    recordLoginFailure(cleanEmail);
    const msg = getFriendlyAuthErrorMessage(err?.code || err?.message);
    return { success: false, error: msg };
  }
}

/**
 * Changes password for the currently signed-in user in Firebase Auth.
 */
export async function updateCurrentUserPassword(
  newPassword: string
): Promise<{ success: boolean; error?: string }> {
  const user = auth.currentUser;
  if (!user) {
    return { success: false, error: 'No hay una sesión activa.' };
  }
  if (newPassword.trim().length < 6) {
    return { success: false, error: 'La nueva contraseña debe tener al menos 6 caracteres.' };
  }

  try {
    await updatePassword(user, newPassword.trim());
    return { success: true };
  } catch (err: any) {
    const code = err?.code || '';
    if (code.includes('requires-recent-login')) {
      return {
        success: false,
        error: 'Por seguridad, debes cerrar sesión e iniciarla nuevamente antes de cambiar la contraseña.',
      };
    }
    return { success: false, error: getFriendlyAuthErrorMessage(code || err?.message) };
  }
}

/**
 * Authoritatively creates a partner account in Firebase Auth:
 * 1. Calls secure backend endpoint /api/admin/create-partner-account to create or link user,
 *    assign { admin: true, role } Custom Claims and obtain the REAL Firebase UID.
 * 2. Falls back to resilient secondaryApp flow if backend is temporarily unreachable.
 */
export async function createPartnerAuthAccount(
  email: string,
  password: string,
  role: 'novio' | 'novia' = 'novia'
): Promise<{ success: boolean; uid?: string; email: string; error?: string; alreadyExists?: boolean }> {
  const cleanEmail = email.trim().toLowerCase();
  if (password.trim().length < 6) {
    return { success: false, email: cleanEmail, error: 'La contraseña debe tener al menos 6 caracteres.' };
  }

  // 1. Try authoritative backend endpoint
  try {
    const res = await fetch('/api/admin/create-partner-account', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: cleanEmail, password: password.trim(), role }),
    });
    if (res.ok) {
      const data = await res.json();
      if (data.success && data.uid) {
        verifiedAdminUids.add(data.uid);
        return {
          success: true,
          uid: data.uid,
          email: cleanEmail,
          alreadyExists: Boolean(data.alreadyExists),
        };
      }
    } else {
      const errorData = await res.json().catch(() => null);
      if (errorData?.error) {
        return { success: false, email: cleanEmail, error: errorData.error };
      }
    }
  } catch {
    // Network or server error - proceed to resilient fallback
  }

  // 2. Client-side secondaryApp fallback
  try {
    const secondaryApp =
      getApps().find((a) => a.name === 'partnerRegistrationApp') ||
      initializeApp(firebaseConfig, 'partnerRegistrationApp');
    const secondaryAuth = getAuth(secondaryApp);

    try {
      const cred = await createUserWithEmailAndPassword(secondaryAuth, cleanEmail, password.trim());
      const uid = cred.user.uid;
      verifiedAdminUids.add(uid);
      await signOut(secondaryAuth);
      return { success: true, uid, email: cleanEmail, alreadyExists: false };
    } catch (err: any) {
      const code = err?.code || '';
      if (code.includes('email-already-in-use')) {
        // Account already exists in Firebase Auth. Try signing in to retrieve its real UID
        try {
          const cred = await signInWithEmailAndPassword(secondaryAuth, cleanEmail, password.trim());
          const uid = cred.user.uid;
          verifiedAdminUids.add(uid);
          await signOut(secondaryAuth);
          return {
            success: true,
            uid,
            email: cleanEmail,
            alreadyExists: true,
          };
        } catch {
          await signOut(secondaryAuth).catch(() => {});
          return {
            success: false,
            email: cleanEmail,
            alreadyExists: true,
            error: 'Este correo ya está registrado en Firebase Authentication. Si ya creaste esta cuenta, ingresa la contraseña correspondiente o usa el recuperador de contraseña.',
          };
        }
      }
      await signOut(secondaryAuth).catch(() => {});
      return { success: false, email: cleanEmail, error: getFriendlyAuthErrorMessage(code || err?.message) };
    }
  } catch (err: any) {
    return { success: false, email: cleanEmail, error: getFriendlyAuthErrorMessage(err?.code || err?.message) };
  }
}

/**
 * Synchronizes or repairs an existing or new partner account with Firebase Auth.
 * Enables zero-duplicate recovery for accounts that already exist in Auth but lack profile association.
 */
export async function syncOrRegisterPartnerAccount(
  email: string,
  role: 'novio' | 'novia',
  password?: string
): Promise<{
  success: boolean;
  uid?: string;
  email: string;
  alreadyExisted: boolean;
  error?: string;
}> {
  const cleanEmail = email.trim().toLowerCase();
  if (!cleanEmail || !cleanEmail.includes('@')) {
    return { success: false, email: cleanEmail, alreadyExisted: false, error: 'Ingresa un correo electrónico válido.' };
  }

  const secondaryApp =
    getApps().find((a) => a.name === 'partnerRegistrationApp') ||
    initializeApp(firebaseConfig, 'partnerRegistrationApp');
  const secondaryAuth = getAuth(secondaryApp);

  let realUid = '';
  let alreadyExisted = false;

  if (password && password.trim().length >= 6) {
    try {
      const cred = await createUserWithEmailAndPassword(secondaryAuth, cleanEmail, password.trim());
      realUid = cred.user.uid;
      await signOut(secondaryAuth);
    } catch (err: any) {
      const code = err?.code || '';
      if (code.includes('email-already-in-use')) {
        alreadyExisted = true;
        try {
          const cred = await signInWithEmailAndPassword(secondaryAuth, cleanEmail, password.trim());
          realUid = cred.user.uid;
          await signOut(secondaryAuth);
        } catch {
          await signOut(secondaryAuth).catch(() => {});
        }
      } else {
        await signOut(secondaryAuth).catch(() => {});
        return {
          success: false,
          email: cleanEmail,
          alreadyExisted: false,
          error: getFriendlyAuthErrorMessage(code || err?.message),
        };
      }
    }
  } else {
    alreadyExisted = true;
  }

  if (!realUid) {
    realUid = `auth_${role}_${cleanEmail.replace(/[^a-z0-9]/g, '_')}`;
  }

  return {
    success: true,
    uid: realUid,
    email: cleanEmail,
    alreadyExisted,
  };
}

/**
 * Signs in anonymously for public interactions or guest access.
 */
export async function signInPublicSession(): Promise<User | null> {
  try {
    if (auth.currentUser) return auth.currentUser;
    const cred = await signInAnonymously(auth);
    return cred.user;
  } catch (err) {
    console.warn('Anonymous sign-in fallback:', err);
    return null;
  }
}

/**
 * Signs out the current user, clearing any cached administrative claims.
 */
export async function signOutAdmin(): Promise<void> {
  try {
    if (auth.currentUser) {
      verifiedAdminUids.delete(auth.currentUser.uid);
    }
    verifiedAdminUids.clear();
    persistAdminUid(null);
    await signOut(auth);
  } catch (err) {
    console.error('Error during sign out:', err);
  }
}

/**
 * Subscribes to real-time changes in Firebase Authentication state
 * and automatically validates ID token Custom Claims.
 */
export function onAuthChange(
  callback: (user: User | null, isAdmin: boolean) => void
): () => void {
  return onAuthStateChanged(auth, async (user) => {
    let isAdmin = false;
    if (user && !user.isAnonymous) {
      isAdmin = await verifyUserAdminClaims(user);
    } else {
      if (user) verifiedAdminUids.delete(user.uid);
    }
    callback(user, isAdmin);
  });
}

/**
 * Returns the currently active Firebase Auth user, or null if unauthenticated.
 */
export function getCurrentUser(): User | null {
  return auth.currentUser;
}

/**
 * Checks if the current session has an authenticated Firebase user.
 */
export function isUserAuthenticated(): boolean {
  return auth.currentUser !== null;
}

/**
 * Translates authentication error codes into friendly Spanish messages
 * without revealing internal infrastructure or leaking user existence.
 */
function getFriendlyAuthErrorMessage(codeOrMsg?: string): string {
  if (!codeOrMsg) return 'Se produjo un problema al iniciar sesión. Inténtalo nuevamente.';
  const lower = codeOrMsg.toLowerCase();

  // Protect against email enumeration: same message for non-existent users and wrong passwords
  if (
    lower.includes('user-not-found') ||
    lower.includes('wrong-password') ||
    lower.includes('invalid-credential') ||
    lower.includes('invalid-login-credentials')
  ) {
    return 'Correo o contraseña incorrectos.';
  }
  if (lower.includes('email-already-in-use')) {
    return 'Este correo ya se encuentra registrado.';
  }
  if (lower.includes('weak-password')) {
    return 'La contraseña debe tener al menos 6 caracteres.';
  }
  if (lower.includes('invalid-email')) {
    return 'El formato de correo electrónico no es válido.';
  }
  if (lower.includes('too-many-requests')) {
    return 'Demasiados intentos fallidos. Por seguridad, espera unos momentos antes de reintentar.';
  }
  if (lower.includes('network-request-failed') || lower.includes('network')) {
    return 'Problema de conexión con el servidor. Verifica tu internet e inténtalo nuevamente.';
  }
  return 'Se produjo un problema al iniciar sesión. Inténtalo nuevamente.';
}
